import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';

import Badge from './components/Badge';
import { GlobalState } from '../../../../store/types';
import { setBadgeCount } from '../../../../store/actions';

import './style.scss';
import React, { useEffect, useMemo, useState } from 'react';
import openLauncher from '../../../../../assets/launcher_button.svg'
import close from '../../../../../assets/close.svg'
import { LAUNCHER_PROMPTS } from '../../../../../../config';

type Props = {
  toggle: () => void;
  chatId: string;
  openLabel: string;
  closeLabel: string;
  closeImg: string;
  openImg: string;
  showBadge?: boolean;
}

const PROMPT_MESSAGES = LAUNCHER_PROMPTS;

// Written by hooks/useLogin.js on login; prompts rotate for an hour after that.
const BETA_LOGIN_TIMESTAMP_KEY = 'evie_beta_login_ts';
const BETA_PROMPT_DURATION_MS = 60 * 60 * 1000;
const PROMPT_ROTATE_INTERVAL_MS = 5500;

function Launcher({ toggle, chatId, openImg, closeImg, openLabel, closeLabel, showBadge }: Props) {
  const dispatch = useDispatch();
  const { showChat, badgeCount } = useSelector((state: GlobalState) => ({
    showChat: state.behavior.showChat,
    badgeCount: state.messages.badgeCount
  }));

  const [isWithinBetaWindow, setIsWithinBetaWindow] = useState(false);
  const [promptIndex, setPromptIndex] = useState(() => Math.floor(Math.random() * PROMPT_MESSAGES.length));

  const prompts = useMemo(() => {
    const shuffled = [...PROMPT_MESSAGES];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const rawTimestamp = window.localStorage.getItem(BETA_LOGIN_TIMESTAMP_KEY);
    let loginTimestamp = rawTimestamp ? Number(rawTimestamp) : Number.NaN;

    if (!Number.isFinite(loginTimestamp)) {
      loginTimestamp = Date.now();
      window.localStorage.setItem(BETA_LOGIN_TIMESTAMP_KEY, loginTimestamp.toString());
    }

    const evaluateWindow = () => {
      const withinWindow = Date.now() - loginTimestamp < BETA_PROMPT_DURATION_MS;
      setIsWithinBetaWindow(withinWindow);
      return withinWindow;
    };

    const shouldContinue = evaluateWindow();

    if (!shouldContinue) return;

    const intervalId = window.setInterval(() => {
      const stillWithin = evaluateWindow();
      if (!stillWithin) {
        window.clearInterval(intervalId);
      }
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!isWithinBetaWindow || showChat || prompts.length <= 1) return undefined;

    const rotationId = window.setInterval(() => {
      setPromptIndex((prevIndex) => (prevIndex + 1) % prompts.length);
    }, PROMPT_ROTATE_INTERVAL_MS);

    return () => {
      window.clearInterval(rotationId);
    };
  }, [isWithinBetaWindow, prompts, showChat]);

  const toggleChat = () => {
    toggle();
    if (!showChat) dispatch(setBadgeCount(0));
  }

  const shouldShowPrompt = !showChat && isWithinBetaWindow && prompts.length > 0;
  const currentPrompt = prompts[promptIndex % prompts.length];

  return (
    <div className="rcw-launcher-wrapper">
      {shouldShowPrompt && (
        <div className="rcw-launcher-prompt" aria-live="polite">
          {currentPrompt}
        </div>
      )}
      <button type="button" className={cn('rcw-launcher', { 'rcw-hide-sm': showChat })} onClick={toggleChat} aria-controls={chatId}>
        {!showChat && showBadge && <Badge badge={badgeCount} />}
        {showChat ?
          <img src={closeImg || close} className="rcw-close-launcher" alt={openLabel} /> :
          <img src={openImg || openLauncher} className="rcw-open-launcher" alt={closeLabel} />
        }
      </button>
    </div>
  );
}

export default Launcher;
